import rules from '../../helpers/assistanceGuideRules';

const SHOWN_AT_KEY = 'assistanceGuideShownAt';
const DEFAULT_COOLDOWN_HOURS = 24;

function loadJSON(key, fallback) {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
}

function saveJSON(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

// Each guide is shown at most once per cooldown window (default 24h) per user.
// Cooldown state is keyed by JWT user_id so an admin/agent impersonating
// multiple testators shares one cooldown bucket. Persisted in localStorage so
// it survives logout/login. Rules flagged `repeatable: true` bypass the
// cooldown and always show when their condition matches (e.g. Upload Successful).
function cooldownHoursFor(rule) {
  if (rule.repeatable) return 0;
  return rule.cooldownHours || DEFAULT_COOLDOWN_HOURS;
}

function isWithinCooldown(rule, shownAt, scopeKey) {
  const hours = cooldownHoursFor(rule);
  if (!hours) return false;
  const scoped = shownAt[scopeKey];
  const last = scoped && scoped[rule.id];
  if (!last) return false;
  return Date.now() - last < hours * 60 * 60 * 1000;
}

export default {
  state: {
    activeGuide: null,
    guideShownAt: loadJSON(SHOWN_AT_KEY, {}),
  },
  mutations: {
    setActiveGuide(state, guide) {
      state.activeGuide = guide;
    },
    recordGuideShown(state, { id, scopeKey }) {
      const scoped = state.guideShownAt[scopeKey] || {};
      state.guideShownAt = {
        ...state.guideShownAt,
        [scopeKey]: { ...scoped, [id]: Date.now() },
      };
      saveJSON(SHOWN_AT_KEY, state.guideShownAt);
    },
    clearActiveGuide(state) {
      state.activeGuide = null;
    },
    resetGuides(state) {
      state.guideShownAt = {};
      state.activeGuide = null;
      localStorage.removeItem(SHOWN_AT_KEY);
    },
  },
  actions: {
    evaluateGuideRules({ commit, state, rootGetters }, payload) {
      const { testator, steps, page, ...context } = payload;
      if (!testator || !steps) {
        return;
      }

      const scopeKey = String(rootGetters.userIdFromToken || 'anonymous');

      // If a guide is already active for this page and its condition still
      // matches, leave it alone. Apollo fires multiple queries in quick
      // succession; without this guard, the rule we just activated is now in
      // its own cooldown window and the next evaluation would clobber it.
      const current = state.activeGuide;
      if (current && current.page === page) {
        const currentRule = rules.find(r => r.id === current.id);
        if (currentRule && currentRule.condition(testator, steps, context)) {
          return;
        }
      }

      const matchingRule = rules
        .filter(rule => rule.page === page)
        .filter(rule => !isWithinCooldown(rule, state.guideShownAt, scopeKey))
        .sort((a, b) => a.priority - b.priority)
        .find(rule => rule.condition(testator, steps, context));

      if (matchingRule) {
        const content = matchingRule.contentFn
          ? matchingRule.contentFn(testator)
          : matchingRule.content;
        if (!matchingRule.repeatable) {
          commit('recordGuideShown', { id: matchingRule.id, scopeKey });
        }
        commit('setActiveGuide', { ...matchingRule, content });
      } else {
        commit('setActiveGuide', null);
      }
    },
    dismissCurrentGuide({ commit }) {
      commit('clearActiveGuide');
    },
    resetGuides({ commit }) {
      commit('resetGuides');
    },
  },
  getters: {
    activeGuide: state => state.activeGuide,
    hasActiveGuide: state => !!state.activeGuide,
  },
};
